/** Extract the human-readable first error message from a graphql-request ClientError. */
export function gqlMessage(err: unknown): string | undefined {
  const res = (err as { response?: { errors?: { message?: string }[] } })?.response
  return res?.errors?.[0]?.message
}

/**
 * The symbolic domain error of the first GraphQL error (`extensions.errorType`, e.g.
 * `CI_TAG_DUPLICATE`), falling back to `extensions.code`, or null when there is neither.
 *
 * Subgraphs built on the platform's ServiceError send BOTH: `code` is a numeric identifier
 * (`INV_70011`) and `errorType` the symbolic name, so branching on `code` alone never matches a
 * domain constant. Subgraphs that only send `code` (e.g. GRAPHQL_VALIDATION_FAILED) still work.
 * Domain-neutral on purpose: invoices, help requests, and any other feature share this one reading.
 */
export function gqlErrorCode(err: unknown): string | null {
  const ext = (
    err as { response?: { errors?: Array<{ extensions?: { code?: unknown; errorType?: unknown } }> } }
  )?.response?.errors?.[0]?.extensions
  if (typeof ext?.errorType === 'string') return ext.errorType
  return typeof ext?.code === 'string' ? ext.code : null
}

/**
 * The `extensions.data` payload of the first GraphQL error — the structured fields a `ServiceError`
 * attaches beyond its symbolic type (e.g. `{ partyId, identityStatus }` on
 * `EM_NATURAL_PERSON_IDENTITY_BLOCKED`, `{ officersWithoutEmail }` on `EM_OFFICER_EMAIL_REQUIRED`).
 * `undefined` when the error carries no `data` block.
 */
export function gqlErrorData(err: unknown): Record<string, unknown> | undefined {
  const ext = (
    err as { response?: { errors?: Array<{ extensions?: { data?: unknown } }> } }
  )?.response?.errors?.[0]?.extensions
  return ext && typeof ext.data === 'object' && ext.data !== null
    ? (ext.data as Record<string, unknown>)
    : undefined
}
